import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { logError } from '../../utils/errorLogger';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, Transition } from '@headlessui/react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { getStatusBadgeClass, getStatusLabel } from '../../utils/status';

const AdminHome = () => {
  const [stats, setStats] = useState({
    users: 0,
    payments: 0,
    shipments: 0,
    consularActs: 0
  });
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  const isRestrictedUser = user?.email === 'contact@cgci-paris.com';
  
  // Variables d'état pour les expéditions
  const [shipments, setShipments] = useState([]);
  const [shipmentError, setShipmentError] = useState(null);
  const [shipmentModal, setShipmentModal] = useState({ open: false, loading: false, shipment: null, error: null });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [documentModal, setDocumentModal] = useState({ open: false, title: '', url: '' });
  
  // Colonnes pour le tableau des expéditions
  const columns = [
    { label: 'Destinataire', key: 'recipient_name' },
    { label: 'Document', key: 'documents_to_be_sent' },
    { label: 'Transporteur', key: 'carrier' },
    { 
      label: 'Statut', 
      key: 'status', 
      type: 'status',
      labels: {
        delivered: 'Livré',
        pending: 'En attente',
        created: 'Expédition créée',
        in_progress: 'En cours',
        returned: 'Retourné'
      }
    },
    { label: 'Date', key: 'created_at', type: 'date' }
  ];
  
  // Classes et libellés de statut sont fournis par utils/status
  
  // Gestion de l&apos;édition d&apos;une expédition
  const handleEdit = (shipment) => {
    window.location.href = `/admin/shipments/edit/${shipment.id}`;
  };
  
  // Handler pour ouvrir la modale et charger les infos d&apos;expédition
  const handleShowShipment = async (shipmentId) => {
    setShipmentModal({ open: true, loading: true, shipment: null, error: null });
    try {
      const { data, error } = await supabase
        .from('shipments')
        .select('*')
        .eq('id', shipmentId)
        .single();
      if (error) {
        setShipmentModal({ open: true, loading: false, shipment: null, error: error.message });
        return;
      }
      setShipmentModal({ open: true, loading: false, shipment: data, error: null });
    } catch (err) {
      setShipmentModal({ open: true, loading: false, shipment: null, error: err.message });
    }
  };
  
  // Handler pour fermer la modale
  const handleCloseShipment = () => {
    setShipmentModal(prev => ({ ...prev, open: false }));
  };
  
  // Fonction pour obtenir une URL signée pour un fichier de stockage Supabase
  const getSignedUrl = async (bucket, filename) => {
    try {
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .createSignedUrl(filename, 300); // URL valide pendant 5 minutes

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'URL signée pour ${filename}:`, error);
      throw error;
    }
  };

  // Fonction pour afficher un document dans une modale
  const handleViewDocument = async (shipment, documentType) => {
    try {
      let url, title, filename;
      
      // Récupérer l'ID de la transaction de paiement associée à cette expédition
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('transaction_id')
        .eq('id', shipment.payment_id)
        .single();
      
      if (paymentError) throw paymentError;
      
      if (documentType === 'receipt') {
        filename = `${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        title = 'Reçu de paiement';
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        title = 'Formulaire d\'expédition';
      }

      setDocumentModal({ open: true, title, url });
    } catch (error) {
      console.error('Erreur lors de l\'affichage du document:', error);
      toast.error(`Erreur lors de l'affichage du document. Le fichier n'existe peut-être pas.`);
    }
  };

  // Fonction pour télécharger un document
  const handleDownloadDocument = async (shipment, documentType) => {
    try {
      let url, filename, downloadName;
      
      // Récupérer l'ID de la transaction de paiement associée à cette expédition
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('transaction_id')
        .eq('id', shipment.payment_id)
        .single();
      
      if (paymentError) throw paymentError;
      
      if (documentType === 'receipt') {
        filename = `${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        downloadName = `Reçu_${paymentData.transaction_id}.pdf`;
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        downloadName = filename;
      }

      // Télécharger le fichier
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Créer un lien temporaire pour le téléchargement
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
      toast.error(`Erreur lors du téléchargement du document. Le fichier n'existe peut-être pas.`);
    }
  };
  
  // Fonction pour fermer la modale de document
  const closeDocumentModal = () => {
    setDocumentModal({ ...documentModal, open: false });
  };
  
  // Gestion de la pagination
  const handlePerPage = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Récupérer le nombre d'utilisateurs
        const { count: usersCount, error: usersError } = await supabase
          .from('users')
          .select('*', { count: 'exact' });

        // Récupérer le nombre de paiements
        const { count: paymentsCount, error: paymentsError } = await supabase
          .from('payments')
          .select('*', { count: 'exact' });

        // Récupérer le nombre d'expéditions
        const { count: shipmentsCount, error: shipmentsError } = await supabase
          .from('shipments')
          .select('*', { count: 'exact' });

        // Récupérer les paiements récents
        const { data: recentPaymentsData, error: recentPaymentsError } = await supabase
          .from('payments')
          .select(`
            id,
            amount,
            payment_status,
            payment_method,
            created_at,
            users:users(firstname, lastname)
          `)
          .order('created_at', { ascending: false })
          .limit(5);

        // Récupérer les expéditions récentes
        const { data: shipmentsData, error: shipmentsDataError } = await supabase
          .from('shipments')
          .select('*')
          .order('created_at', { ascending: false });

        // Calculer le montant total des paiements
        const { data: totalRevenueData, error: totalRevenueError } = await supabase
          .from('payments')
          .select('amount', { count: 'exact' })
          .eq('payment_status', 'succeeded');

        if (usersError || paymentsError || shipmentsError || recentPaymentsError || totalRevenueError || shipmentsDataError) {
          throw new Error('Erreur lors du chargement des données');
        }

        // Calculer la somme totale des paiements
        const totalRevenue = totalRevenueData
          ? totalRevenueData.reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
          : 0;

        // Mettre à jour les stats
        setStats({
          users: usersCount || 0,
          payments: paymentsCount || 0,
          shipments: shipmentsCount || 0,
          consularActs: parseFloat(totalRevenue.toFixed(2)) // Montant total des paiements
        });

        // Transformer les paiements récents pour le tableau
        const formattedRecentPayments = recentPaymentsData.map(payment => ({
          id: payment.id,
          user: `${payment.users.firstname} ${payment.users.lastname}`,
          amount: payment.amount,
          date: payment.created_at,
          status: payment.payment_status,
          method: payment.payment_method
        }));

        setRecentPayments(formattedRecentPayments);
        setShipments(shipmentsData || []);
        setLoading(false);
      } catch (error) {
        logError('Erreur lors du chargement des données du tableau de bord', error);
        setShipmentError(error.message || 'Erreur lors du chargement des expéditions.');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Fonction de gestion des détails d'un paiement
  const handleViewPaymentDetails = (payment) => {
    window.location.href = `/admin/payments/${payment.id}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Tableau de bord</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary-light text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-gray-500 text-sm">Utilisateurs</h3>
              <p className="text-2xl font-semibold">{stats.users}</p>
            </div>
          </div>
          <div className="mt-4 text-center">
            <Link to="/admin/users" className="text-sm text-secondary btn btn-secondary !rounded-pill">Tous les utilisateurs</Link>
          </div>
        </div>
        
        {!isRestrictedUser && (
          <div className="bg-white rounded-lg shadow-md p-6 hidden md:hidden">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-light text-primary">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-gray-500 text-sm">Paiements</h3>
                <p className="text-2xl font-semibold">{stats.payments}</p>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Link to="/admin/payments" className="text-sm text-secondary btn btn-secondary !rounded-pill">Tous les paiements</Link>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary-light text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-gray-500 text-sm">Expéditions</h3>
              <p className="text-2xl font-semibold">{stats.shipments}</p>
            </div>
          </div>
          <div className="mt-4 text-center">
            <Link to="/admin/shipments" className="text-sm text-secondary btn btn-secondary !rounded-pill whitespace-nowrap">Toutes les expéditions</Link>
          </div>
        </div>
      </div>
      
      {/* Quick Actions
      <div className="bg-white rounded-lg shadow-md mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Actions rapides</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AdminButton 
            type="primary" 
            to="/admin/users/new" 
            icon="add"
          >
            Ajouter un utilisateur
          </AdminButton>
          
          <AdminButton 
            type="primary" 
            to="/admin/consular-acts/new" 
            icon="add"
          >
            Ajouter un acte consulaire
          </AdminButton>
          
          <AdminButton 
            type="primary" 
            to="/admin/shipments/new" 
            icon="add"
          >
            Enregistrer une expédition
          </AdminButton>
        </div>
      </div> */}
      
      {/* Liste des expéditions */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Expéditions récentes</h2>
          <Link to="/admin/shipments" className="text-sm text-secondary hover:underline">
            Voir toutes
          </Link>
        </div>
        
        {/* Filtres et pagination */}
        <div className="bg-white p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="perPage" className="text-sm font-medium text-gray-700">Afficher:</label>
            <select
              id="perPage"
              className="border border-gray-300 rounded-md text-sm p-1"
              value={perPage}
              onChange={handlePerPage}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        

        
        <div className="sm:hidden">
          {shipmentError && (
            <div className="px-6 py-4 text-center text-red-500">{shipmentError}</div>
          )}

          {!shipmentError && shipments.length === 0 && (
            <div className="px-6 py-4 text-center text-gray-500">Aucune expédition trouvée.</div>
          )}

          {!shipmentError && shipments.slice(0, perPage).map((shipment) => (
            <div key={shipment.id} className="border-t border-gray-200 p-4">
              <div className="flex flex-col gap-2">
                <div className="w-full">
                  <div className="font-semibold text-gray-900 whitespace-pre-line break-words w-full">{shipment.recipient_name}</div>
                  <div className="text-xs text-gray-500 mt-1 whitespace-pre-line break-words w-full">{shipment.recipient_address}</div>
                </div>
                <div className="w-full flex justify-end">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipment.status)}`}>
                    {getStatusLabel(shipment.status)}
                  </span>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500">Transporteur</span>
                  <span className="text-sm text-gray-900 font-medium">{shipment.carrier || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500">Date</span>
                  <span className="text-sm text-gray-900 font-medium">
                    {shipment.created_at ? format(new Date(shipment.created_at), 'dd/MM/yyyy') : '—'}
                  </span>
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-2">
                <button
                  onClick={() => handleShowShipment(shipment.id)}
                  className="w-full px-3 py-2 bg-[#0b8e36] text-white rounded hover:bg-green-700 whitespace-nowrap flex items-center justify-center gap-2"
                  title="Voir les détails de l&apos;expédition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Détails
                </button>
                <button
                  onClick={() => handleEdit(shipment)}
                  className="w-full px-3 py-2 bg-[#e88c00] text-white rounded hover:bg-orange-700 whitespace-nowrap flex items-center justify-center gap-2"
                  title="Modifier l&apos;expédition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Modifier
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden sm:block">
          {/* Shipments Table */}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th 
                    key={index} 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shipmentError && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-6 py-4 text-center text-red-500">{shipmentError}</td>
                </tr>
              )}
              {!shipmentError && shipments.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-6 py-4 text-center text-gray-500">Aucune expédition trouvée.</td>
                </tr>
              )}
              {!shipmentError && shipments.slice(0, perPage).map((shipment) => (
                <tr key={shipment.id}>
                  {columns.map((column, index) => (
                    <td key={index} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {column.type === 'status' ? (
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipment[column.key])}`}>
                          {getStatusLabel(shipment[column.key])}
                        </span>
                      ) : column.type === 'date' ? (
                        shipment[column.key] ? format(new Date(shipment[column.key]), 'dd/MM/yyyy') : '—'
                      ) : column.key === 'recipient_name' ? (
                        <div>
                          <div className="font-medium whitespace-pre-line break-words">{shipment.recipient_name}</div>
                          <div className="text-xs text-gray-500 mt-1 whitespace-pre-line break-words">{shipment.recipient_address}</div>
                        </div>
                      ) : column.key === 'documents_to_be_sent' ? (
                        <div>
                          {(() => {
                            let docs = shipment.documents_to_be_sent;
                            if (typeof docs === 'string') {
                              try {
                                docs = JSON.parse(docs);
                              } catch {
                                docs = [];
                              }
                            }
                            if (Array.isArray(docs) && docs.length > 0) {
                              return (
                                <div className="flex flex-col gap-1">
                                  {docs.map((doc, idx) => (
                                    <div key={doc.act_id || idx} className="flex items-center gap-1.5">
                                      <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                                      <span className="text-xs">{doc.act_title || (typeof doc === 'string' ? doc : JSON.stringify(doc))}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return <span className="italic text-gray-400 text-xs">Aucun document</span>;
                          })()}
                        </div>
                      ) : (
                        shipment[column.key]
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleShowShipment(shipment.id)}
                        className="px-3 py-1 bg-[#0b8e36] text-white rounded hover:bg-green-700 whitespace-nowrap flex items-center gap-1.5 inline-flex"
                        title="Voir les détails de l&apos;expédition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Détails
                      </button>
                      <button
                        onClick={() => handleEdit(shipment)}
                        className="px-3 py-1 bg-[#e88c00] text-white rounded hover:bg-orange-700 whitespace-nowrap flex items-center gap-1.5 inline-flex"
                        title="Modifier l&apos;expédition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {shipments.length > perPage && (
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 border-t border-gray-200">
            <div className="text-xs sm:text-sm text-gray-700">
              Affichage de <span className="font-medium">1</span> à <span className="font-medium">{Math.min(perPage, shipments.length)}</span> sur <span className="font-medium">{shipments.length}</span> expéditions
            </div>
            <div className="flex justify-end space-x-2">
              <button
                className={`px-3 py-1 rounded ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                aria-label="Page précédente"
              >
                <span className="hidden sm:inline">Précédent</span>
                <span className="inline sm:hidden">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L8.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>
              <button
                className={`px-3 py-1 rounded ${page === Math.ceil(shipments.length / perPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}
                onClick={() => setPage(page + 1)}
                disabled={page === Math.ceil(shipments.length / perPage)}
                aria-label="Page suivante"
              >
                <span className="hidden sm:inline">Suivant</span>
                <span className="inline sm:hidden">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Modale d'informations d'expédition */}
      <Transition show={shipmentModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={handleCloseShipment}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
                <div className="flex justify-between items-center bg-[#0b8e36] p-4 text-white">
                  <Dialog.Title as="h3" className="text-xl font-bold">
                    Détails de l&apos;expédition
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-white hover:text-gray-200 transition-colors"
                    onClick={handleCloseShipment}
                  >
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="p-6">
                  {shipmentModal.loading ? (
                    <div className="flex justify-center items-center h-40">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#e88c00]"></div>
                    </div>
                  ) : shipmentModal.error ? (
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-600 text-center font-medium">{shipmentModal.error}</div>
                  ) : shipmentModal.shipment ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {/* Colonne Statut & Tracking */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                          <span className="font-bold text-lg text-[#0b8e36]">Statut de l&apos;expédition</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Numéro de suivi</div>
                              <div className="font-semibold">
                                {shipmentModal.shipment.tracking_number && shipmentModal.shipment.tracking_number.startsWith('CD')
                                  ? 'En attente'
                                  : shipmentModal.shipment.tracking_number || 'Non spécifié'}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0z" /><path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10" /><path d="M1 8h4" /><path d="M16 8h4M16 16h2a2 2 0 002-2v-3a4 4 0 00-4-4h-2" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Transporteur</div>
                              <div className="font-semibold">{shipmentModal.shipment.carrier || 'Non spécifié'}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Statut</div>
                              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipmentModal.shipment.status)}`}>
                                {getStatusLabel(shipmentModal.shipment.status)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Date d&apos;expédition</div>
                              <div className="font-semibold">{shipmentModal.shipment.shipping_date ? format(new Date(shipmentModal.shipment.shipping_date), 'dd/MM/yyyy') : 'Non spécifiée'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne Destinataire */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Destinataire</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full flex items-center justify-center w-10 h-10">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0112 0v2"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Nom</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_name}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full flex items-center justify-center w-10 h-10">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 21s-6-5.686-6-10a6 6 0 1112 0c0 4.314-6 10-6 10z"/><circle cx="12" cy="11" r="2.5"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Adresse</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_address}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full flex items-center justify-center w-10 h-10">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92V19a2 2 0 01-2.18 2A19.72 19.72 0 013 5.18 2 2 0 015 3h2.09a2 2 0 012 1.72c.13.81.36 1.6.68 2.36a2 2 0 01-.45 2.11L8.09 10.91a16 16 0 006 6l1.72-1.72a2 2 0 012.11-.45c.76.32 1.55.55 2.36.68a2 2 0 011.72 2V19z"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Téléphone</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_phone || 'Non spécifié'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne Documents */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 min-w-6 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Documents à expédier</span>
                        </div>
                        
                        {(() => {
                          let docs = shipmentModal.shipment.documents_to_be_sent;
                          if (typeof docs === 'string') {
                            try {
                              docs = JSON.parse(docs);
                            } catch {
                              docs = [];
                            }
                          }
                          if (Array.isArray(docs) && docs.length > 0) {
                            return (
                              <div className="space-y-2">
                                {docs.map((doc, idx) => (
                                  <div key={doc.act_id || idx} className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                                    <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                                      <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                                    </div>
                                    <div className="font-medium">{doc.act_title || (typeof doc === 'string' ? doc : JSON.stringify(doc))}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center justify-center h-24 bg-white rounded-md shadow-sm">
                              <span className="text-gray-400 italic">Aucun document</span>
                            </div>
                          );
                        })()}
                        
                        {shipmentModal.shipment.return_status && (
                          <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-100 flex items-center gap-3">
                            <div className="bg-red-100 p-2 rounded-full">
                              <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 14l-4-4 4-4"/><path d="M5 10h11a4 4 0 110 8h-1"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-red-500 font-medium">Statut de retour</div>
                              <div className="font-semibold text-red-600">Retourné {shipmentModal.shipment.return_reason && <span>({shipmentModal.shipment.return_reason})</span>}</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Documents à télécharger */}
                        <div className="mt-6 space-y-4">
                          {/* Reçu de paiement */}
                          <div className="border rounded-lg p-3 bg-white shadow-sm">
                            <h4 className="font-medium text-base mb-2 text-[#0b8e36]">Reçu de paiement</h4>
                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => handleViewDocument(shipmentModal.shipment, 'receipt')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Voir
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(shipmentModal.shipment, 'receipt')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0b8e36] text-white rounded hover:bg-[#0b8e36]/90 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Télécharger
                              </button>
                            </div>
                          </div>
                          
                          {/* Formulaire d'expédition */}
                          <div className="border rounded-lg p-3 bg-white shadow-sm">
                            <h4 className="font-medium text-base mb-2 text-[#e88c00]">Formulaire d&apos;expédition</h4>
                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => handleViewDocument(shipmentModal.shipment, 'shipping')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Voir
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(shipmentModal.shipment, 'shipping')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#e88c00] text-white rounded hover:bg-[#e88c00]/90 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Télécharger
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                
                <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-100 mt-4">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-[#0b8e36] border border-transparent rounded-md hover:bg-[#097a2e] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0b8e36] transition-colors"
                    onClick={handleCloseShipment}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      
      {/* Paiements récents */}
      {!isRestrictedUser && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8 hidden md:hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Paiements récents</h2>
          <Link to="/admin/payments" className="text-sm text-secondary hover:underline">
            Voir tous
          </Link>
        </div>
        
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Méthode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {recentPayments.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">Aucun paiement trouvé</td>
              </tr>
            )}
            {recentPayments.map((payment) => {
              const user = { firstname: payment.user.split(' ')[0], lastname: payment.user.split(' ')[1] };
              return (
                <tr key={payment.id} onClick={() => handleViewPaymentDetails(payment)} className="cursor-pointer hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.date ? new Date(payment.date).toLocaleDateString() : ''}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secondary" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 12c2.7 0 4.5-1.8 4.5-4.5S14.7 3 12 3 7.5 4.8 7.5 7.5 9.3 12 12 12zm0 2c-3 0-9 1.5-9 4.5V21h18v-2.5c0-3-6-4.5-9-4.5z"/>
                        </svg>
                        {user.firstname} {user.lastname}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.method || 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <span className={
                      payment.status === 'succeeded' ? 'text-green-600' : 
                      payment.status === 'pending' ? 'text-yellow-600' : 
                      'text-red-600'
                    }>
                      {payment.status === 'succeeded' ? 'Payé' : 
                       payment.status === 'pending' ? 'En attente' : 
                       'Échoué'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      {/* Modal pour afficher les PDF */}
      <Transition show={documentModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeDocumentModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrer le modal */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {documentModal.title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={closeDocumentModal}
                  >
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mt-2 bg-gray-100 rounded-lg p-1 h-[70vh]">
                  <iframe
                    src={documentModal.url}
                    className="w-full h-full rounded"
                    title={documentModal.title}
                  />
                </div>
                
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    onClick={closeDocumentModal}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default AdminHome;
